import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import * as _ from 'lodash';

import IUserPermissions = Authorization.IUserPermissions;
import { BehaviorSubject, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { UserPermissions } from '../models';
import { ProductFeatures } from '../enums';
import { BaseEntityService } from 'src/shared/services/base-entity.service';
import { IUserPermissionsModel } from '../interfaces/user-permissions.interface';

const TOKEN_FIELD = 'token';

const API = environment.ZONEIQ_AUTHORIZATION_SERVICE + '/api/v1/auth';

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService extends BaseEntityService<UserPermissions> {
    private readonly userPermissionsSubject = new BehaviorSubject<UserPermissions>(null);

    readonly userPermissions$ = this.userPermissionsSubject.asObservable().pipe(distinctUntilChanged());

    constructor(private readonly cookieService: CookieService, private readonly http: HttpClient) {
        super(UserPermissions);
    }

    get token() {
        return this.cookieService.get(TOKEN_FIELD);
    }

    get userPermissions() {
        return this.userPermissionsSubject.value;
    }

    get hasUserPermissions() {
        return !!this.userPermissionsSubject.value;
    }

    get hasProjectFeatures() {
        return _.isArray(_.get(this, 'userPermissionsSubject.value.Features'));
    }

    get getCanPublishZCDraft() {
        return this.hasUserPermissions && !!this.userPermissions.findFeatureByName(ProductFeatures.ECI_PUBLISH_DRAFT);
    }

    get getCanExportToXSLX() {
        return (
            this.hasUserPermissions && !!this.userPermissions.findFeatureByName(ProductFeatures.ECI_EXPORT_ZC_TO_XLSX)
        );
    }

    removeToken() {
        this.cookieService.delete(TOKEN_FIELD, '/', '.gridics.com');
    }

    logout() {
        const url = `${API}/signout`;

        return this.http.get<object>(url).subscribe(response => {
            this.removeToken();
            const umtServiceURL = _.get(response, 'data.umtServiceURL', '');
            if (umtServiceURL) {
                window.location.assign(
                    `${umtServiceURL}${this.getAuthServiceUrl()}/api/v1/authenticate?redirectUrl=${
                        window.location.href
                    }`
                );
            }
        });
    }

    private getAuthServiceUrl() {
        return environment.ZONEIQ_AUTHORIZATION_SERVICE.indexOf('http') === 0
            ? environment.ZONEIQ_AUTHORIZATION_SERVICE
            : `${window.location.origin}/${environment.ZONEIQ_AUTHORIZATION_SERVICE}`;
    }

    getUserFeatures(): Observable<IUserPermissionsModel> {
        const url = `${API}/features`;

        return this.http.get<object>(url).pipe(
            map(response => response as IUserPermissions),
            map(userPermissions => {
                const result = this.initIfDataExists(userPermissions);
                this.userPermissionsSubject.next(result);
                return result;
            })
        );
    }

    checkIfFeatureGranted(prodcutFeature: ProductFeatures): Observable<boolean> {
        if (!prodcutFeature) {
            return of(false);
        }

        return this.userPermissions$.pipe(
            filter(userPermissions => !!userPermissions),
            map(userPermissions => !!userPermissions.findFeatureByName(prodcutFeature))
        );
    }
}
